import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import Layout from '../components/Layout/Layout';
import StatsCard from '../components/ui/StatsCard';
import Badge from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import { adminApi } from '../config/axios';
import { dateFormat, useDocumentTitle } from '../config/utils';
import { useAuth } from '../contexts/AuthContext';
import { deriveDeviceCounts } from '../utils/deviceCounts';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler);

const FILTERS = [
  { label: 'This Week',  value: 'weekly' },
  { label: 'This Month', value: 'monthly' },
  { label: 'This Year',  value: 'lastYear' },
  { label: 'All Time',   value: 'allTime' },
];
const INVOICE_TYPES = ['GIFT', 'STRIP', 'TRIAL', 'BANK'];

const chartDefaults = {
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#6B7280', font: { size: 11 } } },
    y: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#6B7280', font: { size: 11 } } },
  },
  responsive: true,
  maintainAspectRatio: false,
};

export default function Dashboard() {
  useDocumentTitle('Dashboard');
  const { user } = useAuth();
  const [stats, setStats]         = useState({});
  const [invoiceStats, setInvoiceStats] = useState({});
  const [activity, setActivity]   = useState([]);
  const [filter, setFilter]       = useState('allTime');
  const [loading, setLoading]     = useState(true);

  // The "Online Devices" stat card reads `stats` — previously fetched once
  // on mount and never refreshed, the stalest online-status surface found.
  // Poll at the same 15s cadence as the rest; only the initial fetch drives
  // the loading spinner so the periodic refreshes don't flicker the page.
  useEffect(() => {
    let cancelled = false;

    const fetchStats = (isInitial) => {
      adminApi.get('/dashboard')
        .then(res => {
          if (cancelled) return;
          const d = res.data?.data ?? {};
          setStats(d);
          setInvoiceStats(d.invoiceStatistics ?? {});
          setActivity(d.recentActivity ?? []);
        })
        .finally(() => { if (!cancelled && isInitial) setLoading(false); });
    };

    fetchStats(true);
    const interval = setInterval(() => fetchStats(false), 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const revenueData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [{
      label: 'Revenue',
      data: invoiceStats.monthlyRevenue ?? Array(12).fill(0),
      borderColor: '#7C6FFF',
      backgroundColor: 'rgba(124,111,255,0.08)',
      fill: true,
      tension: 0.4,
      pointRadius: 3,
      pointBackgroundColor: '#7C6FFF',
    }],
  };

  const deviceData = {
    labels: ['Online', 'Offline', 'Warning'],
    datasets: [{
      data: [stats.onlineDevices ?? 0, stats.offlineDevices ?? 0, stats.warningDevices ?? 0],
      backgroundColor: ['rgba(0,212,170,0.8)', 'rgba(239,68,68,0.8)', 'rgba(245,166,35,0.8)'],
      borderWidth: 0,
    }],
  };

  const currentFilter = invoiceStats[filter] ?? [];
  const deviceCounts = deriveDeviceCounts(stats);

  return (
    <Layout>
      {/* Welcome banner */}
      <div className="card-padded flex items-center justify-between mb-6 glow-accent">
        <div>
          <h1 className="text-xl font-bold text-text-main">
            Welcome back, <span className="text-gradient">{user?.name}</span>
          </h1>
          <p className="text-text-muted text-sm mt-1">
            Here's what's happening with your platform today.
          </p>
        </div>
        <div className="text-xs text-text-muted">{new Date().toLocaleDateString('en', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatsCard title="Total Companies" value={stats.companies} color="accent" loading={loading}
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16" /></svg>} />
        <StatsCard title="Total Devices" value={stats.devices} color="teal" loading={loading}
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>} />
        {/* ONLINE only (2026-10-07 presence-states follow-up) —
            stats.onlineDevices itself is unchanged (still includes
            standby/app_closed); the truly-online-only count comes from
            deriveDeviceCounts(), never read directly off the backend field. */}
        <StatsCard title="Online Devices" value={deviceCounts.onlineOnly} color="teal" loading={loading}
          subtitle={stats.devices ? `${Math.round((deviceCounts.onlineOnly / stats.devices) * 100)}% of total` : undefined}
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728M8.464 15.536a5 5 0 010-7.072m7.072 0a5 5 0 010 7.072M13 12a1 1 0 11-2 0 1 1 0 012 0z" /></svg>} />
        <StatsCard title="Not Showing Now" value={deviceCounts.notShowingNow} color="gold" loading={loading}
          subtitle={
            <>
              {deviceCounts.standby} in standby ·{' '}
              <span style={deviceCounts.appClosed > 0 ? { color: 'var(--gold, #F5A623)', fontWeight: 700 } : undefined}>
                {deviceCounts.appClosed} app closed
              </span>
            </>
          }
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>} />
        <StatsCard title="Total Users" value={stats.users} color="gold" loading={loading}
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197" /></svg>} />
      </div>

      {/* Revenue Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 card-padded">
          <h3 className="section-title mb-4">Revenue Overview</h3>
          <div className="h-56">
            <Line data={revenueData} options={chartDefaults} />
          </div>
        </div>

        {/* Device Health */}
        <div className="card-padded">
          <h3 className="section-title mb-4">Device Health</h3>
          {loading ? <Spinner /> : (
            <div className="space-y-3">
              {[
                { label: 'Online',  value: stats.onlineDevices ?? 0,  color: 'teal',   bg: 'bg-teal/10' },
                { label: 'Offline', value: stats.offlineDevices ?? 0, color: 'danger', bg: 'bg-danger/10' },
                { label: 'Warning', value: stats.warningDevices ?? 0, color: 'gold',   bg: 'bg-gold/10' },
              ].map(item => {
                const total = stats.devices || 1;
                const pct = Math.round((item.value / total) * 100);
                return (
                  <div key={item.label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-text-muted">{item.label}</span>
                      <span className="text-text-main font-medium">{item.value}</span>
                    </div>
                    <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full bg-${item.color}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Invoice Statistics */}
      <div className="card-padded mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-title">Invoice Statistics</h3>
          <div className="flex gap-1">
            {FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  filter === f.value ? 'bg-accent text-white' : 'text-text-muted hover:text-text-main hover:bg-white/[0.06]'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {INVOICE_TYPES.map(type => {
            const stat = currentFilter.find(s => s.type === type) ?? {};
            return (
              <div key={type} className="bg-white/[0.03] rounded-xl p-4 border border-white/[0.06]">
                <Badge variant="muted">{type}</Badge>
                <p className="text-2xl font-bold text-text-main mt-2">{stat.count ?? 0}</p>
                <p className="text-xs text-text-muted mt-1">
                  Total paid: <span className="text-teal">${(stat.totalPaid ?? 0).toFixed(2)}</span>
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      {activity.length > 0 && (
        <div className="card-padded">
          <h3 className="section-title mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {activity.slice(0, 8).map((item, i) => (
              <div key={i} className="flex items-start gap-3 py-2 border-b border-white/[0.05] last:border-0">
                <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-main">{item.description}</p>
                  <p className="text-xs text-text-muted mt-0.5">{dateFormat(item.created_at)}</p>
                </div>
                {item.user && <span className="text-xs text-text-muted">{item.user}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}
